// Updated MainLayout.js with all syntax errors fixed
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import usePageTracking from '../hooks/usePageTracking';
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Menu,
  Chip,
  MenuItem,
  Badge,
  useTheme,
  alpha,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  NightsStay as DarkModeIcon,
  WbSunny as LightModeIcon,
  Menu as MenuIcon,
  Cloud as CloudIcon,
  AttachMoney as CostIcon,
  Computer as InstancesIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  History as HistoryIcon,
  Computer as ServerIcon,
  Search as SearchIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { CONSTANTS } from '../utils/constants';
import { useAppData } from '../contexts/AppDataContext';
import { useAuth } from '../hooks/useAuth';
import NotificationMenu from '../components/common/NotificationMenu';

// Import the logo image
import eventusLogo from '../assets/company/logo.png'; // Adjust path to your logo file
import eventusIcon from '../assets/company/icon.png'; // Adjust path to your logo file

// Drawer width - expanded and collapsed states
const drawerWidth = 240;
const collapsedDrawerWidth = 64;

export default function MainLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, hasPermission } = useAuth();

  // Define isEsdlSelected function before using it in state initialization
  // Check if any ESDL route is selected
  const isEsdlSelected = () => {
    return location.pathname === '/esdl-monitoring' ||
      location.pathname.startsWith('/esdl-monitoring/') ||
      location.pathname === '/opensearch-monitoring';
  };

  // State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [ociMenuOpen, setOciMenuOpen] = useState(false);
  const [esdlMenuOpen, setEsdlMenuOpen] = useState(isEsdlSelected());

  // Derived variables
  const userMenuOpen = Boolean(anchorEl);
  const currentDrawerWidth = drawerCollapsed ? collapsedDrawerWidth : drawerWidth;

  // Handle mobile drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Handle collapse/expand drawer
  const toggleDrawerCollapsed = () => {
    setDrawerCollapsed(!drawerCollapsed);
  };

  // Handle user menu open
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle user menu close
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);

    // Keep ESDL menu open when navigating to ESDL-related paths
    if (path === '/esdl-monitoring' || path.startsWith('/esdl-monitoring/') || path === '/opensearch-monitoring') {
      console.log('Navigating to ESDL path, keeping menu open');
      setEsdlMenuOpen(true);
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
  };

  // Toggle OCI submenu
  const handleToggleOciMenu = () => {
    setOciMenuOpen(!ociMenuOpen);
  };

  // Toggle ESDL submenu
  const handleToggleEsdlMenu = () => {
    console.log('Toggling ESDL menu from', esdlMenuOpen, 'to', !esdlMenuOpen);
    setEsdlMenuOpen(!esdlMenuOpen);
  };

  // Determine if a nav item is selected
  const isSelected = (paths) => {
    if (Array.isArray(paths)) {
      return paths.some(path => location.pathname === path || location.pathname.startsWith(path));
    }
    return location.pathname === paths || location.pathname.startsWith(paths);
  };

  // Check if any OCI route is selected
  const isOciSelected = () => {
    return isSelected(['/oci', '/oci/costs', '/oci/instances']);
  };

  // Update esdlMenuOpen when the location changes
  useEffect(() => {
    if (isEsdlSelected()) {
      console.log('ESDL route selected, setting menu open');
      setEsdlMenuOpen(true);
    }

    // Debug permissions and menu state
    console.log('Has VIEW_OPENSEARCH permission:', hasPermission('VIEW_OPENSEARCH'));
    console.log('Current ESDL menu state:', esdlMenuOpen);
    console.log('Current location:', location.pathname);
  }, [location.pathname, hasPermission]);

  // Add a separate useEffect for initial mount
  useEffect(() => {
    // Check if we're on an ESDL route when the component mounts
    if (isEsdlSelected()) {
      console.log('Initial mount - ESDL route detected, opening menu');
      setEsdlMenuOpen(true);
    }
  }, []); // Empty dependency array means this runs once on mount

  // Enable automatic page tracking
  usePageTracking();

  // Drawer content
  const drawerContent = (
    <>
      <Box
        sx={{
          height: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: drawerCollapsed ? 1 : 2,
          backgroundColor: 'transparent',
          position: 'relative',
        }}
      >

        {!drawerCollapsed && (
          <img
            src={eventusLogo}
            alt="Eventus Logo"
            style={{
              height: '50px',
              width: 'auto',
            }}
          />
        )}

        {drawerCollapsed && (
          <img
            src={eventusIcon}
            alt="Eventus Icon"
            style={{
              height: '35px',
              width: 'auto',
            }}
          />
        )}


        <IconButton
          onClick={toggleDrawerCollapsed}
          size="small"
          sx={{
            position: 'fixed',
            left: drawerCollapsed ? collapsedDrawerWidth - 2 : drawerWidth - 2,
            top: 40,
            zIndex: 1400,

            color: 'white',
            width: 34,
            height: 34,
            padding: 0.5,
            transform: 'translateX(-50%)',
            borderRadius: drawerCollapsed ? '0 50% 50% 0' : '50% 0 0 50%',
            transition: 'left 0.3s ease',
            '&:hover': {

            },
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          {drawerCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Divider sx={{ opacity: 0.1 }} />

      {!drawerCollapsed && (
        <Typography
          variant="overline"
          sx={{
            px: 3,
            py: 2,
            color: theme.palette.text.secondary,
            fontSize: '0.6875rem',
            letterSpacing: 1,
          }}
        >
          MONITORING
        </Typography>
      )}

      <List sx={{ px: drawerCollapsed ? 0.5 : 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            selected={isSelected('/')}
            onClick={() => handleNavigation('/')}
            sx={{
              borderRadius: '10px',
              mb: 0.5,
              justifyContent: drawerCollapsed ? 'center' : 'flex-start',
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            <Tooltip title={drawerCollapsed ? "Dashboard" : ""} placement="right">
              <ListItemIcon sx={{
                color: isSelected('/') ? theme.palette.primary.main : theme.palette.text.secondary,
                minWidth: drawerCollapsed ? 0 : 40,
                mr: drawerCollapsed ? 0 : 2,
                justifyContent: 'center',
              }}>
                <DashboardIcon />
              </ListItemIcon>
            </Tooltip>
            {!drawerCollapsed && (
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  fontWeight: isSelected('/') ? 'medium' : 'normal',
                  color: isSelected('/') ? theme.palette.primary.main : undefined,
                }}
              />
            )}
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          {(hasPermission('VIEW_OCI_DASHBOARD') || hasPermission('VIEW_OCI_COSTS') || hasPermission('VIEW_OCI_INSTANCES')) && (
            <ListItemButton
              selected={isOciSelected()}
              onClick={handleToggleOciMenu}
              sx={{
                borderRadius: '10px',
                mb: 0.5,
                justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              <Tooltip title={drawerCollapsed ? "OCI Monitoring" : ""} placement="right">
                <ListItemIcon sx={{
                  color: isOciSelected() ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: drawerCollapsed ? 0 : 40,
                  mr: drawerCollapsed ? 0 : 2,
                  justifyContent: 'center',
                }}>
                  <CloudIcon />
                </ListItemIcon>
              </Tooltip>
              {!drawerCollapsed && (
                <>
                  <ListItemText
                    primary="OCI Monitoring"
                    primaryTypographyProps={{
                      fontWeight: isOciSelected() ? 'medium' : 'normal',
                      color: isOciSelected() ? theme.palette.primary.main : undefined,
                    }}
                  />
                  {ociMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>
          )}
        </ListItem>

        {/* OCI Submenu */}
        {!drawerCollapsed && (
          <Collapse in={ociMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {hasPermission('VIEW_OCI_DASHBOARD') && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isSelected('/oci')}
                    onClick={() => handleNavigation('/oci')}
                    sx={{
                      pl: 4,
                      borderRadius: '10px',
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        },
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: isSelected('/oci') ? theme.palette.primary.main : theme.palette.text.secondary,
                      minWidth: 30,
                    }}>
                      <DashboardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Overview"
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isSelected('/oci') ? 'medium' : 'normal',
                        color: isSelected('/oci') ? theme.palette.primary.main : undefined,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {hasPermission('VIEW_OCI_COSTS') && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isSelected('/oci/costs')}
                    onClick={() => handleNavigation('/oci/costs')}
                    sx={{
                      pl: 4,
                      borderRadius: '10px',
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        },
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: isSelected('/oci/costs') ? theme.palette.primary.main : theme.palette.text.secondary,
                      minWidth: 30,
                    }}>
                      <CostIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Costs"
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isSelected('/oci/costs') ? 'medium' : 'normal',
                        color: isSelected('/oci/costs') ? theme.palette.primary.main : undefined,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {hasPermission('VIEW_OCI_INSTANCES') && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isSelected('/oci/instances')}
                    onClick={() => handleNavigation('/oci/instances')}
                    sx={{
                      pl: 4,
                      borderRadius: '10px',
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        },
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: isSelected('/oci/instances') ? theme.palette.primary.main : theme.palette.text.secondary,
                      minWidth: 30,
                    }}>
                      <InstancesIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Instances"
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isSelected('/oci/instances') ? 'medium' : 'normal',
                        color: isSelected('/oci/instances') ? theme.palette.primary.main : undefined,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>
        )}

        {/* ESDL Monitoring Button */}
        <ListItem disablePadding>
          {hasPermission('VIEW_ESDL_MONITORING') && (
            <ListItemButton
              selected={isEsdlSelected()}
              onClick={handleToggleEsdlMenu}
              sx={{
                borderRadius: '10px',
                mb: 0.5,
                justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                border: esdlMenuOpen ? `1px solid ${theme.palette.primary.main}` : 'none',
              }}
            >
              <Tooltip title={drawerCollapsed ? "ESDL Monitoring" : ""} placement="right">
                <ListItemIcon sx={{
                  color: isEsdlSelected() ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: drawerCollapsed ? 0 : 40,
                  mr: drawerCollapsed ? 0 : 2,
                  justifyContent: 'center',
                }}>
                  <CloudIcon />
                </ListItemIcon>
              </Tooltip>
              {!drawerCollapsed && (
                <>
                  <ListItemText
                    primary="ESDL Monitoring"
                    primaryTypographyProps={{
                      fontWeight: isEsdlSelected() ? 'medium' : 'normal',
                      color: isEsdlSelected() ? theme.palette.primary.main : undefined,
                    }}
                  />
                  {esdlMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>
          )}
        </ListItem>

        {/* ESDL Submenu */}
        {!drawerCollapsed && (
          <Collapse in={esdlMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname === '/esdl-monitoring'}
                  onClick={() => handleNavigation('/esdl-monitoring')}
                  sx={{
                    pl: 4,
                    borderRadius: '10px',
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: location.pathname === '/esdl-monitoring' ? theme.palette.primary.main : theme.palette.text.secondary,
                    minWidth: 30,
                  }}>
                    <ServerIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="App Servers"
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: location.pathname === '/esdl-monitoring' ? 'medium' : 'normal',
                      color: location.pathname === '/esdl-monitoring' ? theme.palette.primary.main : undefined,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {hasPermission('VIEW_OPENSEARCH') && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={location.pathname === '/opensearch-monitoring'}
                    onClick={() => handleNavigation('/opensearch-monitoring')}
                    sx={{
                      pl: 4,
                      borderRadius: '10px',
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        },
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                      backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                      border: `1px dashed ${alpha(theme.palette.secondary.main, 0.5)}`,
                    }}
                  >
                    <ListItemIcon sx={{
                      color: location.pathname === '/opensearch-monitoring' ? theme.palette.primary.main : theme.palette.text.secondary,
                      minWidth: 30,
                    }}>
                      <SearchIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="OpenSearch"
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === '/opensearch-monitoring' ? 'medium' : 'normal',
                        color: location.pathname === '/opensearch-monitoring' ? theme.palette.primary.main : undefined,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>
        )}

        {/* Production Redirections - visible to RD and Admin users */}
        {hasPermission('VIEW_PRODUCTION_REDIRECTIONS') && (
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/production-redirections')}
              onClick={() => handleNavigation('/production-redirections')}
              sx={{
                borderRadius: '10px',
                mb: 0.5,
                justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              <Tooltip title={drawerCollapsed ? "Production Redirections" : ""} placement="right">
                <ListItemIcon sx={{
                  color: isSelected('/production-redirections') ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: drawerCollapsed ? 0 : 40,
                  mr: drawerCollapsed ? 0 : 2,
                  justifyContent: 'center',
                }}>
                  <PublicIcon />
                </ListItemIcon>
              </Tooltip>
              {!drawerCollapsed && (
                <ListItemText
                  primary="Productions"
                  primaryTypographyProps={{
                    fontWeight: isSelected('/production-redirections') ? 'medium' : 'normal',
                    color: isSelected('/production-redirections') ? theme.palette.primary.main : undefined,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        )}

        {/* User Management - only visible to users with MANAGE_USERS permission */}
        {hasPermission('MANAGE_USERS') && (
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/users')}
              onClick={() => handleNavigation('/users')}
              sx={{
                borderRadius: '10px',
                mb: 0.5,
                justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              <Tooltip title={drawerCollapsed ? "User Management" : ""} placement="right">
                <ListItemIcon sx={{
                  color: isSelected('/users') ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: drawerCollapsed ? 0 : 40,
                  mr: drawerCollapsed ? 0 : 2,
                  justifyContent: 'center',
                }}>
                  <PeopleIcon />
                </ListItemIcon>
              </Tooltip>
              {!drawerCollapsed && (
                <ListItemText
                  primary="User Management"
                  primaryTypographyProps={{
                    fontWeight: isSelected('/users') ? 'medium' : 'normal',
                    color: isSelected('/users') ? theme.palette.primary.main : undefined,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        )}

        {/* Audit Logs - only visible to users with VIEW_AUDIT_LOGS permission */}
        {hasPermission('VIEW_AUDIT_LOGS') && (
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/audit-logs')}
              onClick={() => handleNavigation('/audit-logs')}
              sx={{
                borderRadius: '10px',
                mb: 0.5,
                justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              <Tooltip title={drawerCollapsed ? "Audit Logs" : ""} placement="right">
                <ListItemIcon sx={{
                  color: isSelected('/audit-logs') ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: drawerCollapsed ? 0 : 40,
                  mr: drawerCollapsed ? 0 : 2,
                  justifyContent: 'center',
                }}>
                  <HistoryIcon />
                </ListItemIcon>
              </Tooltip>
              {!drawerCollapsed && (
                <ListItemText
                  primary="Audit Logs"
                  primaryTypographyProps={{
                    fontWeight: isSelected('/audit-logs') ? 'medium' : 'normal',
                    color: isSelected('/audit-logs') ? theme.palette.primary.main : undefined,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        )}

        {/* Settings item */}
        {/* {drawerCollapsed && (
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/settings')}
              onClick={() => handleNavigation('/settings')}
              sx={{
                borderRadius: '10px',
                mb: 0.5,
                justifyContent: 'center',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              <Tooltip title="Settings" placement="right">
                <ListItemIcon sx={{
                  color: isSelected('/settings') ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: 0,
                  justifyContent: 'center',
                }}>
                  <SettingsIcon />
                </ListItemIcon>
              </Tooltip>
            </ListItemButton>
          </ListItem> */}
        {/* )} */}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Show the user profile section at the bottom */}
      {!drawerCollapsed && (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              borderRadius: '10px',
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 'bold',
              }}
            >
              {/* Display first letter of username, or 'U' if not available */}
              {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <Box sx={{ ml: 1.5, flexGrow: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {/* Display actual username, falling back through different possible structures */}
                {user?.username ||
                  (user?.session_response?.username) ||
                  (user?.email ? user.email.split('@')[0] : 'User')}
              </Typography>
            </Box>

            {/* Add logout button directly in the user section */}
            <Tooltip title="Logout">
              <IconButton
                size="small"
                onClick={handleLogout}
                sx={{
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.2),
                  },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Settings button */}
            {/* <Tooltip title="Settings">
              <IconButton 
                size="small" 
                sx={{ 
                  ml: 1,
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
                onClick={() => handleNavigation('/settings')}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}
          </Box>
        </Box>
      )}

      {/* In collapsed mode, show just the user avatar at bottom */}
      {drawerCollapsed && (
        <Box

        >
          <Tooltip title="Logout" placement="right">
            <IconButton
              onClick={handleLogout}
              sx={{
                color: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.2),
                },
              }}
              size="small"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            onClick={handleUserMenuOpen}
          >
            {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
          </Avatar>
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{
      display: 'flex',
      backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f5f5f5',
      color: theme.palette.text.primary,
      minHeight: '100vh',
    }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          zIndex: 1100, // Make sure this is less than drawer but higher than content
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Add sidebar toggle button in the AppBar */}
          {/* In collapsed mode, add an expand button at the bottom */}
          {drawerCollapsed && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 1,
                mt: 2
              }}
            >

            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                display: { xs: 'none', md: 'block' },
                fontWeight: 'bold',
              }}
            >
              {location.pathname === '/' && 'Dashboard'}
              {location.pathname === '/hosts' && 'Host Management'}
              {location.pathname === '/clients' && 'Client Management'}
              {location.pathname === '/alerts' && 'System Alerts'}
              {location.pathname === '/settings' && 'Settings'}
              {location.pathname === '/oci' && 'OCI Dashboard'}
              {location.pathname === '/ocicosts' && 'OCI Cost Analysis'}
              {location.pathname === '/oci/instances' && 'OCI Instances'}
              {location.pathname === '/esdl-monitoring' && 'ESDL Monitoring Dashboard'}
              {location.pathname.includes('/esdl-monitoring/host/') && `ESDL Host: ${location.pathname.split('/')[3]}`}
              {location.pathname.includes('/region/') && `Region: ${location.pathname.split('/')[2]}`}
              {location.pathname === '/production-redirections' && 'Production Redirections'}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationMenu />

            <Tooltip title={theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
              <IconButton
                color="inherit"
                onClick={() => {
                  // Toggle theme implementation
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: userMenuOpen ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.background.default, 0.5),
                borderRadius: '20px',
                padding: '4px 8px 4px 4px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                }}
              >
                {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  ml: 1,
                  fontWeight: 'medium',
                  display: { xs: 'none', md: 'block' }
                }}
              >
                {user?.username || 'User'}
              </Typography>

              {/* Add logout button next to user name in header */}
              <Tooltip title="Logout">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent opening user menu
                    handleLogout();
                  }}
                  sx={{
                    ml: 1,
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: currentDrawerWidth,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))`,
            border: 'none',
            boxShadow: `1px 0 10px rgba(0, 0, 0, 0.2)`,
            zIndex: 1200,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
          marginLeft: { xs: 0, sm: `${currentDrawerWidth}px` },
          backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f5f5f5',
          color: theme.palette.text.primary,
          minHeight: '100vh',
          transition: 'margin-left 0.3s ease, width 0.3s ease',
        }}
      >
        <Toolbar /> {/* Spacer for the AppBar */}
        <Outlet />
      </Box>
    </Box>
  );
}